import { ProjectCallsInsightType } from "@prisma/client";
import { useCallback, useEffect, useRef, useState } from "react";
import { Tooltip, TooltipPosition } from "@/app/(components)/generics/Tootlip/Tooltip";
import { ModalStore } from "@/app/stores/ModalStore";
import { TKeyCallAnalysis as TKey } from "@/app/translations/keys/TKeyCallAnalysis";
import { useT } from "@/app/translations/useT";
import { CallScopeFilter } from "@/app/types/call/CallScopeFilter";
import { PageParams } from "@/app/types/common/DefaultProps";
import { InsightCallSource } from "@/app/types/project/InsightCallSource";
import { ProjectInsightItem } from "@/app/types/project/ProjectInsightItem";
import { ProjectPatternItem } from "@/app/types/project/ProjectPatternItem";
import { UStyles } from "@/app/utils/UStyles";
import styles from "./InsightItem.module.css";

type Props = {
  insight: ProjectInsightItem | ProjectPatternItem;
  lang: string;
  params?: PageParams;
  source?: InsightCallSource;
  filter?: CallScopeFilter;
};

export default function InsightItem(props: Props) {
  const { insight, params, source = InsightCallSource.INSIGHT, filter = {} } = props;
  const T = useT();
  const [isOverflowing, setIsOverflowing] = useState(false);
  const labelRef = useRef<HTMLSpanElement>(null);

  const isVerbatim = "type" in insight && insight.type === ProjectCallsInsightType.VERBATIM;
  const rawText =
    isVerbatim && "quotes" in insight
      ? ((insight.quotes as string[])?.[0] ?? insight.label)
      : insight.label;
  const displayText = isVerbatim ? `"${rawText}"` : rawText;

  const checkOverflow = useCallback(() => {
    if (labelRef.current) {
      const element = labelRef.current;
      setIsOverflowing(element.scrollWidth > element.clientWidth);
    }
  }, []);

  useEffect(() => {
    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [checkOverflow]);

  return (
    <div className={styles.item} onMouseEnter={checkOverflow}>
      <Tooltip
        trigger={
          <span ref={labelRef} className={styles.itemLabel}>
            {displayText}
          </span>
        }
        content={displayText}
        position={TooltipPosition.TOP}
        className={styles.tooltipOverride}
        disabled={!isOverflowing}
      />
      <Tooltip
        trigger={
          <span
            className={UStyles.classNames(styles.itemValue, params && styles.itemValueClickable)}
            onClick={
              params && insight.callIdsCount > 0
                ? () =>
                    ModalStore.getState().openInsightCalls(
                      params,
                      insight.id,
                      source,
                      insight.label,
                      filter,
                    )
                : undefined
            }
          >
            {insight.occurrenceRate !== null && insight.occurrenceRate !== undefined
              ? (T(TKey.CALL_ANALYSIS_INSIGHT_PERCENTAGE) as string).replace(
                  "{{value}}",
                  Math.round(insight.occurrenceRate * 100).toString(),
                )
              : insight.callIdsCount}
          </span>
        }
        content={`${insight.callIdsCount} ${
          insight.callIdsCount > 1
            ? T(TKey.CALL_ANALYSIS_COUNT_PLURAL)
            : T(TKey.CALL_ANALYSIS_COUNT_SINGULAR)
        }`}
        position={TooltipPosition.TOP}
        className={styles.tooltipValueOverride}
      />
    </div>
  );
}
